import { ArrowRight, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { SiteFooter, SiteHeader } from "@/components/layout/SiteChrome";

export interface GuideFaq {
  q: string;
  a: string;
}

export interface GuideSection {
  /** Rendered as the h2. Phrased as the question a reader would actually ask. */
  heading: string;
  /** Paragraphs of prose. Kept as separate strings so each stays quotable on its own. */
  body: string[];
  /** Optional checklist rendered under the prose. */
  points?: string[];
}

interface GuidePageProps {
  eyebrow: string;
  headline: string;
  /** One-paragraph answer placed directly under the h1, before anything else. */
  standfirst: string;
  /** The short answer, up front. This is the block most likely to be quoted verbatim. */
  keyPoints: string[];
  sections: GuideSection[];
  faqs: GuideFaq[];
  updated: string;
  readingMinutes: number;
  related?: Array<{ href: string; label: string }>;
  /** The published pages the guide was written from. Shown as outbound links. */
  sources?: Array<{ href: string; label: string }>;
}

/**
 * Layout for an explainer guide.
 *
 * Deliberately not the service-page layout: a guide earns a citation by
 * answering the question in the first screen, so the summary sits above the
 * detail and the call to action stays at the bottom, quiet and single.
 */
export function GuidePage({
  eyebrow,
  headline,
  standfirst,
  keyPoints,
  sections,
  faqs,
  updated,
  readingMinutes,
  related = [],
  sources = [],
}: GuidePageProps) {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader tone="ink" />

      <main>
        <article>
          <header data-tone="ink" className="tone-ink">
            <div className="mx-auto max-w-3xl px-5 pb-12 pt-14 lg:px-8 lg:pt-20">
              <div className="hero-eyebrow">
                <span className="hero-eyebrow__line" />
                <span>{eyebrow}</span>
              </div>
              <h1 className="mt-7 text-[clamp(2.1rem,4.6vw,3.4rem)] leading-[1.02]">{headline}</h1>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{standfirst}</p>
              <p className="mt-7 text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">
                Updated {updated} · {readingMinutes} min read
              </p>
            </div>
          </header>

          {/* The short answer, before the detail — this is what gets quoted. */}
          <section className="border-b border-border bg-secondary/40">
            <div className="mx-auto max-w-3xl px-5 py-10 lg:px-8">
              <h2 className="text-xs uppercase tracking-[0.25em] text-primary">The short answer</h2>
              <ul className="mt-6 space-y-3">
                {keyPoints.map((point) => (
                  <li key={point} className="flex gap-3 text-[0.95rem] leading-relaxed">
                    <Check className="mt-1 size-4 shrink-0 text-primary" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <div className="mx-auto max-w-3xl px-5 py-14 lg:px-8">
            {sections.map((section) => (
              <section key={section.heading} className="mb-14 last:mb-0">
                <h2 className="text-2xl leading-tight sm:text-3xl">{section.heading}</h2>
                {section.body.map((paragraph) => (
                  <p key={paragraph} className="mt-5 leading-relaxed text-muted-foreground">
                    {paragraph}
                  </p>
                ))}
                {section.points && section.points.length > 0 && (
                  <ul className="mt-6 space-y-3 border-l-2 border-primary/30 pl-5">
                    {section.points.map((point) => (
                      <li
                        key={point}
                        className="text-[0.95rem] leading-relaxed text-muted-foreground"
                      >
                        {point}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <section className="border-t border-border">
            <div className="mx-auto max-w-3xl px-5 py-14 lg:px-8">
              <h2 className="text-2xl leading-tight sm:text-3xl">Common questions</h2>
              <dl className="mt-9 divide-y divide-border border-y border-border">
                {faqs.map((faq) => (
                  <div key={faq.q} className="py-6">
                    <dt className="font-semibold">{faq.q}</dt>
                    <dd className="mt-3 leading-relaxed text-muted-foreground">{faq.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          {sources.length > 0 && (
            <section className="border-t border-border">
              <div className="mx-auto max-w-3xl px-5 py-12 lg:px-8">
                <h2 className="text-xs uppercase tracking-[0.25em] text-primary">Sources</h2>
                <ul className="mt-6 space-y-3">
                  {sources.map((source) => (
                    <li key={source.href}>
                      <a
                        href={source.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="leading-relaxed text-muted-foreground underline underline-offset-4 hover:text-foreground"
                      >
                        {source.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {related.length > 0 && (
            <section className="border-t border-border">
              <div className="mx-auto max-w-3xl px-5 py-12 lg:px-8">
                <h2 className="text-xs uppercase tracking-[0.25em] text-primary">
                  Related reading
                </h2>
                <ul className="mt-6 space-y-3">
                  {related.map((item) => (
                    <li key={item.href}>
                      <a
                        href={item.href}
                        className="inline-flex items-center gap-2 font-semibold text-primary hover:text-foreground"
                      >
                        {item.label} <ArrowRight className="size-4" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}
        </article>

        <section className="border-t border-border">
          <div className="mx-auto max-w-3xl px-5 py-14 text-center lg:px-8">
            <h2 className="text-2xl leading-tight sm:text-3xl">
              Want this reviewed in your own account?
            </h2>
            <p className="mx-auto mt-4 max-w-xl leading-relaxed text-muted-foreground">
              An account review examines campaign structure, creative and measurement, then returns
              a prioritised list of what to change and why.
            </p>
            <Button variant="brand" size="xl" className="mt-8" asChild>
              <a href="/#audit">
                Request a fit review <ArrowRight className="size-4" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
